import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, Sparkles, Flame, TrendingUp, Zap, Crown, Award, MessageCircle, Heart, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { CharacterCard } from "@/components/CharacterCard";
import { FavoritesGrid } from "@/components/FavoritesGrid";
import { useAuth } from "@/context/AuthContext";

const TRAITS = [
  { label: "Tsundere", tag: "Tsundere" },
  { label: "Dominant", tag: "Dominant" },
  { label: "Fürsorglich", tag: "Caring" },
  { label: "Geheimnisvoll", tag: "Mysterious" },
  { label: "Verspielt", tag: "Playful" },
  { label: "Beschützend", tag: "Protective" },
  { label: "Kühl", tag: "Cold" },
  { label: "Schüchtern", tag: "Shy" },
  { label: "Charmant", tag: "Charming" },
  { label: "Treu", tag: "Loyal" },
  { label: "Ergeben", tag: "Devoted" },
  { label: "Elegant", tag: "Elegant" },
];

export default function Home() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cotd, setCotd] = useState(null);
  const [recs, setRecs] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [alltime, setAlltime] = useState([]);
  const [boardMode, setBoardMode] = useState("week");
  const [activeCat, setActiveCat] = useState("All");
  const [activeGender, setActiveGender] = useState("All");
  const [activeTag, setActiveTag] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showFavorites, setShowFavorites] = useState(false);

  const isDefaultView = activeCat === "All" && activeGender === "All" && !activeTag && !search && !showFavorites;

  const fetchCharacters = async (cat, q, gender, tag) => {
    setLoading(true);
    const params = {};
    if (cat && cat !== "All") params.category = cat;
    if (q) params.search = q;
    if (gender && gender !== "All") params.gender = gender;
    if (tag) params.tag = tag;
    const res = await api.get("/characters", { params });
    setCharacters(res.data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/categories").then((res) => setCategories(["All", ...res.data]));
    api.get("/characters-of-the-day").then((res) => setCotd(res.data)).catch(() => {});
    api.get("/recommendations").then((res) => setRecs(res.data || [])).catch(() => {});
    api.get("/trending-weekly").then((res) => setWeekly(res.data || [])).catch(() => {});
    api.get("/trending-alltime").then((res) => setAlltime(res.data || [])).catch(() => {});
    fetchCharacters("All", "", "All", "");
    if (localStorage.getItem("jb_token")) refreshUser().catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (showFavorites) {
        setLoading(true);
        api.get("/favorites").then((res) => { setCharacters(res.data); setLoading(false); }).catch(() => setLoading(false));
      } else {
        fetchCharacters(activeCat, search, activeGender, activeTag);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [activeCat, search, activeGender, activeTag, showFavorites]);

  const startChatWith = async (character) => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      const res = await api.post("/chats", { character_id: character.id });
      navigate(`/chat/${res.data.session_id}`);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Dieser Charakter ist nur für VIP-Mitglieder");
        navigate("/vip");
      } else {
        toast.error("Chat konnte nicht gestartet werden");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-fuchsia-300 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Powered by Claude AI
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Chat with characters
            <br />
            that feel{" "}
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
              alive
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-xl mx-auto">
            Dive into immersive roleplay with hundreds of AI personalities, or craft your own
            character and bring them to life.
          </p>
        </motion.div>

        {/* Search */}
        <div className="mt-9 max-w-xl mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            data-testid="character-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search characters, tags, worlds..."
            className="w-full bg-[#131520] border border-white/10 focus:border-fuchsia-500/50 rounded-full pl-12 pr-5 py-3.5 text-white placeholder-slate-500 outline-none transition-all focus:shadow-[0_0_25px_rgba(236,72,153,0.12)]"
          />
        </div>

        {user && !user.unlimited && (
          <div className="mt-6 max-w-xl mx-auto" data-testid="message-usage">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="flex items-center gap-1.5 text-slate-400">
                <Zap className="w-3.5 h-3.5 text-cyan-400" /> Nachrichten heute
              </span>
              <span className="text-slate-300 font-medium">
                {user.messages_today?.toLocaleString("de-DE")} / {user.daily_limit?.toLocaleString("de-DE")}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-pink-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, (user.messages_today / user.daily_limit) * 100)}%` }}
              />
            </div>
            <button
              onClick={() => (window.location.href = "/vip")}
              data-testid="usage-upgrade"
              className="mt-2 text-xs text-fuchsia-400 hover:text-fuchsia-300 font-medium flex items-center gap-1 mx-auto"
            >
              <Crown className="w-3 h-3" /> Unbegrenzt chatten mit VIP
            </button>
          </div>
        )}
      </section>

      {/* Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-1.5 mb-3" data-testid="gender-filter">
          {["All", "Female", "Male", "Non-binary"].map((g) => (
            <button
              key={g}
              data-testid={`gender-${g.toLowerCase()}`}
              onClick={() => { setActiveGender(g); setShowFavorites(false); }}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                activeGender === g && !showFavorites
                  ? "bg-white text-[#090A0F]"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              {g === "All" ? "Alle" : g === "Female" ? "Weiblich" : g === "Male" ? "Männlich" : "Divers"}
            </button>
          ))}
          {user && (
            <button
              data-testid="favorites-toggle"
              onClick={() => setShowFavorites((v) => !v)}
              className={`ml-auto flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                showFavorites
                  ? "bg-rose-500/20 text-rose-300 border border-rose-400/40"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${showFavorites ? "fill-rose-400" : ""}`} /> Favoriten
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-${cat.toLowerCase()}`}
              onClick={() => { setActiveCat(cat); setShowFavorites(false); }}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                activeCat === cat
                  ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-md shadow-fuchsia-500/25"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Personality traits */}
        <div className="flex items-center gap-2 mt-1 overflow-x-auto pb-1 scrollbar-hide" data-testid="trait-filter">
          <span className="flex items-center gap-1 text-[11px] font-semibold text-fuchsia-300 uppercase tracking-wider flex-shrink-0">
            <Heart className="w-3.5 h-3.5" /> Charakter
          </span>
          {TRAITS.map((t) => (
            <button
              key={t.tag}
              data-testid={`trait-${t.tag.toLowerCase()}`}
              onClick={() => setActiveTag((cur) => (cur === t.tag ? "" : t.tag))}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeTag === t.tag
                  ? "bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40"
                  : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Character of the Day + For You (default view only) */}
      {isDefaultView && cotd && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8" data-testid="character-of-the-day">
          <div className="flex items-center gap-2 mb-4 text-slate-300">
            <Award className="w-5 h-5 text-amber-400" />
            <h2 className="font-display text-xl font-bold text-white">Charakter des Tages</h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => startChatWith(cotd)}
            data-testid="cotd-banner"
            className="group cursor-pointer relative overflow-hidden rounded-3xl border border-amber-400/20 bg-[#131520] hover:border-amber-400/50 transition-all"
          >
            <div className="flex flex-col sm:flex-row">
              <div className="relative sm:w-72 md:w-80 flex-shrink-0 aspect-[16/10] sm:aspect-auto sm:min-h-[240px] overflow-hidden">
                <img src={cotd.avatar_url} alt={cotd.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-[#131520] via-transparent to-transparent" />
              </div>
              <div className="flex-1 p-6 sm:p-8 flex flex-col justify-center">
                <span className="inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/30 mb-3">
                  <Sparkles className="w-3 h-3" /> {cotd.category}
                </span>
                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-white">{cotd.name}</h3>
                <p className="text-sm text-slate-400 mt-1">{cotd.title}</p>
                <p className="text-sm text-slate-300 mt-3 line-clamp-2 italic max-w-xl">{cotd.greeting}</p>
                <span className="mt-5 inline-flex items-center gap-2 self-start bg-gradient-to-r from-amber-500 to-fuchsia-600 text-white font-semibold text-sm rounded-full px-5 py-2.5 shadow-lg shadow-fuchsia-500/20 group-hover:scale-105 transition-transform">
                  <MessageCircle className="w-4 h-4" /> Jetzt chatten
                </span>
              </div>
            </div>
          </motion.div>
        </section>
      )}

      {isDefaultView && user && recs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="for-you-section">
          <div className="flex items-center gap-2 mb-6 text-slate-300">
            <Heart className="w-5 h-5 text-fuchsia-400" />
            <h2 className="font-display text-xl font-bold text-white">Für dich empfohlen</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {recs.slice(0, 8).map((c, i) => (
              <CharacterCard key={c.id} character={c} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Top 10 leaderboard (weekly / all-time) */}
      {isDefaultView && (weekly.length > 0 || alltime.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10" data-testid="top-weekly-section">
          <div className="flex flex-wrap items-center gap-3 mb-6 text-slate-300">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              <h2 className="font-display text-xl font-bold text-white">Top 10</h2>
            </div>
            <div className="flex items-center gap-1.5 ml-auto sm:ml-2" data-testid="board-toggle">
              <button
                data-testid="board-week"
                onClick={() => setBoardMode("week")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  boardMode === "week"
                    ? "bg-amber-500/20 text-amber-200 border border-amber-400/40"
                    : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
                }`}
              >
                Diese Woche
              </button>
              <button
                data-testid="board-alltime"
                onClick={() => setBoardMode("all")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  boardMode === "all"
                    ? "bg-amber-500/20 text-amber-200 border border-amber-400/40"
                    : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
                }`}
              >
                Alle Zeit
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(boardMode === "week" ? weekly : alltime).slice(0, 10).map((c, i) => {
              const rankColor =
                i === 0
                  ? "from-amber-400 to-yellow-600 text-black"
                  : i === 1
                  ? "from-slate-200 to-slate-400 text-black"
                  : i === 2
                  ? "from-amber-700 to-orange-800 text-white"
                  : "from-white/10 to-white/5 text-slate-300";
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  data-testid={`top-weekly-${i + 1}`}
                  onClick={() => startChatWith(c)}
                  className="group flex items-center gap-4 bg-[#131520] hover:bg-[#1A1D2C] border border-white/5 hover:border-fuchsia-500/40 rounded-2xl p-3 cursor-pointer transition-all"
                >
                  <span
                    className={`flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br ${rankColor} flex items-center justify-center font-display font-extrabold text-sm shadow`}
                  >
                    {i + 1}
                  </span>
                  <img
                    src={c.avatar_url}
                    alt={c.name}
                    className="w-14 h-14 rounded-xl object-cover object-top border border-white/10 flex-shrink-0 group-hover:scale-105 transition-transform"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold text-white truncate flex items-center gap-1.5">
                      {c.name}
                      {c.is_vip && <Crown className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />}
                    </h3>
                    <p className="text-xs text-slate-400 truncate">{c.title}</p>
                  </div>
                  <span className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-fuchsia-300">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {boardMode === "week"
                      ? (c.weekly_chats > 0 ? c.weekly_chats : c.chat_count || 0)
                      : (c.chat_count || 0)}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-20">
        <div className="flex items-center gap-2 mb-6 text-slate-300">
          {showFavorites ? <Heart className="w-5 h-5 text-rose-400" /> : search ? <Search className="w-5 h-5 text-fuchsia-400" /> : <TrendingUp className="w-5 h-5 text-fuchsia-400" />}
          <h2 className="font-display text-xl font-bold text-white">
            {showFavorites
              ? "Deine Favoriten"
              : search
              ? `Results for "${search}"`
              : activeTag
              ? (TRAITS.find((t) => t.tag === activeTag)?.label || activeTag)
              : activeCat === "All"
              ? "Trending Now"
              : activeCat}
          </h2>
          <Flame className="w-4 h-4 text-amber-400" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="no-results">
            No characters found. Try a different search.
          </div>
        ) : showFavorites ? (
          <FavoritesGrid favorites={characters} onChange={setCharacters} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {characters.map((c, i) => (
              <CharacterCard key={c.id} character={c} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
